/**
 * i18next setup of the client panel (NFR-08: Uzbek Latin default, Russian, English). Two namespaces: `client`
 * (this app, i18n/locales) and `ui` (design system). The chosen language is remembered per browser.
 */
import { UI_I18N_NAMESPACE, uiLocaleResources } from '@person-platform/ui';
import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import englishClientMessages from './locales/en.json';
import russianClientMessages from './locales/ru.json';
import uzbekClientMessages from './locales/uz.json';

export const CLIENT_I18N_NAMESPACE = 'client';
export const SUPPORTED_LANGUAGES = ['uz', 'ru', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: SupportedLanguage = 'uz';
const LANGUAGE_STORAGE_KEY = 'pc.language';

export const clientLocaleResources = {
  uz: uzbekClientMessages,
  ru: russianClientMessages,
  en: englishClientMessages,
} as const;

export function isSupportedLanguage(candidate: unknown): candidate is SupportedLanguage {
  return typeof candidate === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(candidate);
}

export function readStoredLanguage(): SupportedLanguage | null {
  try {
    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isSupportedLanguage(storedLanguage) ? storedLanguage : null;
  } catch {
    return null;
  }
}

export function storeLanguage(language: SupportedLanguage): void {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Remembering the language is a convenience only.
  }
}

export function createClientI18n(
  initialLanguage: SupportedLanguage = readStoredLanguage() ?? DEFAULT_LANGUAGE,
): I18nInstance {
  const i18nInstance = i18next.createInstance();
  void i18nInstance.use(initReactI18next).init({
    lng: initialLanguage,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    ns: [CLIENT_I18N_NAMESPACE, UI_I18N_NAMESPACE],
    defaultNS: CLIENT_I18N_NAMESPACE,
    resources: {
      uz: { [CLIENT_I18N_NAMESPACE]: clientLocaleResources.uz, [UI_I18N_NAMESPACE]: uiLocaleResources.uz },
      ru: { [CLIENT_I18N_NAMESPACE]: clientLocaleResources.ru, [UI_I18N_NAMESPACE]: uiLocaleResources.ru },
      en: { [CLIENT_I18N_NAMESPACE]: clientLocaleResources.en, [UI_I18N_NAMESPACE]: uiLocaleResources.en },
    },
    interpolation: { escapeValue: false },
    returnNull: false,
    initAsync: false,
  });
  i18nInstance.on('languageChanged', (languageCode) => {
    document.documentElement.lang = languageCode;
  });
  return i18nInstance;
}

/** Translation hook of the client namespace. */
export function useClientTranslation() {
  return useTranslation(CLIENT_I18N_NAMESPACE);
}
